import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { useToast } from "../../Hooks/useToast";
import { formatDateTimeForDisplay } from "../../Utils/timeUtils";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import EditIcon from "@mui/icons-material/Edit";

import AlternativeHeader from "../../Components/Headres/Alternative_Header";
import ReservationsCard from "../../Components/Cards/Reservations_Card";
import NotesModal from "./Add_Notes_Modal";

import { fetchGetAllReservationsByIdApi } from "../../Redux/Reservations_Slice";
import { Updatereservation, FetchEditReservations } from "../../Apis/Reservations/Reservations";

const ViewReservations = () => {
  const param = useParams();
  const dispatch = useDispatch();
  const data = useSelector((state) => state.Reservations);
  const navigate = useNavigate();
  const { toastSuccess, toastError } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [adminNote, setAdminNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  // حالات التعديل
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [isUpdatingTime, setIsUpdatingTime] = useState(false);
  const [editFormData, setEditFormData] = useState({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    customerStreet: "",
    customerPLZ: "",
    customerCity: "",
    status: "",
    adminNote: "",
    note: "",
    dateOfReservation: null,
    dateEndOfReservation: null
  });

  useEffect(() => {
    if (param.id) {
      dispatch(fetchGetAllReservationsByIdApi(param.id));
    }
  }, [param.id]);

  useEffect(() => {
    const note = data.data?.data?.adminNote || "";
    setAdminNote(note);
  }, [data.data]);

  const handleSaveNotes = async () => {
    setIsLoading(true);
    try {
      // إرسال البيانات الأساسية مع adminNote لتجنب validation errors
      const updateData = {
        adminNote,
        // إضافة البيانات المطلوبة من البيانات الحالية
        customerName: data?.data?.data?.customerName || data?.data?.data?.userName || "",
        customerEmail: data?.data?.data?.customerEmail || data?.data?.data?.userId?.email || "",
        customerPhone: data?.data?.data?.customerPhone || "+49 000 000 0000"
      };
      
      const response = await Updatereservation(param.id, updateData);
      
      if (response.status === 200) {
        toastSuccess("Der Vorgang war erfolgreich!");
        dispatch(fetchGetAllReservationsByIdApi(param.id));
        setModalOpen(false);
      } else {
        const errorMsg = response?.data?.message || response?.error || `Fehler: Status ${response.status}`;
        toastError(errorMsg);
      }
    } catch (error) {
      const serverMessage = error?.response?.data?.message || error?.message || "Ein Fehler ist aufgetreten!";
      toastError(serverMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getGermanStatus = (status) => {
    switch (status) {
      case "accepted":
        return { label: "Angenommen", color: "#B6E6C1" };
      case "rejected":
        return { label: "Abgelehnt", color: "#E6B6B6" };
      case "pending":
        return { label: "Ausstehend", color: "#F0E0C0" };
      default:
        return { label: "Unbekannt", color: "#CCCCCC" };
    }
  };

  const germanStatus = getGermanStatus(data?.data?.data?.status);

  // دوال التعديل
  const handleEdit = () => {
    const reservation = data?.data?.data;
    if (!reservation) return;
    
    console.log("🔍 EDIT: Opening edit dialog for reservation:", reservation.reservationNumber);
    console.log("🔍 EDIT: Original dateOfReservation:", reservation.dateOfReservation);
    console.log("🔍 EDIT: Original dateEndOfReservation:", reservation.dateEndOfReservation);
    
    setEditingReservation(reservation);
    
    // Convert UTC dates to local Date objects for DateTimePicker
    let startDate = null;
    let endDate = null;
    
    if (reservation.dateOfReservation) {
      const utcDate = new Date(reservation.dateOfReservation);
      startDate = new Date(
        utcDate.getUTCFullYear(),
        utcDate.getUTCMonth(),
        utcDate.getUTCDate(),
        utcDate.getUTCHours(),
        utcDate.getUTCMinutes(),
        utcDate.getUTCSeconds()
      );
    }
    
    if (reservation.dateEndOfReservation) {
      const utcDate = new Date(reservation.dateEndOfReservation);
      endDate = new Date(
        utcDate.getUTCFullYear(),
        utcDate.getUTCMonth(),
        utcDate.getUTCDate(),
        utcDate.getUTCHours(),
        utcDate.getUTCMinutes(),
        utcDate.getUTCSeconds()
      );
    }
    
    setEditFormData({
      customerName: reservation.customerName || reservation.userName || "",
      customerEmail: reservation.customerEmail || reservation.userId?.email || "",
      customerPhone: reservation.customerPhone || "",
      customerStreet: reservation.customerStreet || "",
      customerPLZ: reservation.customerPLZ || "",
      customerCity: reservation.customerCity || "",
      status: reservation.status || "",
      adminNote: reservation.adminNote || "",
      note: reservation.note || "",
      dateOfReservation: startDate,
      dateEndOfReservation: endDate
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingReservation(null);
    setEditFormData({
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      customerStreet: "",
      customerPLZ: "",
      customerCity: "",
      status: "",
      adminNote: "",
      note: "",
      dateOfReservation: null,
      dateEndOfReservation: null
    });
  };

  const handleSaveEdit = async (payload) => {
    if (!editingReservation || !param.id) return;
    setIsLoading(true);
    
    // Convert dates to UTC ISO strings
    let dateOfReservationUTC = payload.dateOfReservation;
    let dateEndOfReservationUTC = payload.dateEndOfReservation;
    
    if (payload.dateOfReservation instanceof Date) {
      dateOfReservationUTC = new Date(Date.UTC(
        payload.dateOfReservation.getFullYear(),
        payload.dateOfReservation.getMonth(),
        payload.dateOfReservation.getDate(),
        payload.dateOfReservation.getHours(),
        payload.dateOfReservation.getMinutes(),
        payload.dateOfReservation.getSeconds()
      )).toISOString();
    }
    
    if (payload.dateEndOfReservation instanceof Date) {
      dateEndOfReservationUTC = new Date(Date.UTC(
        payload.dateEndOfReservation.getFullYear(),
        payload.dateEndOfReservation.getMonth(),
        payload.dateEndOfReservation.getDate(),
        payload.dateEndOfReservation.getHours(),
        payload.dateEndOfReservation.getMinutes(),
        payload.dateEndOfReservation.getSeconds()
      )).toISOString();
    }
    
    const processedPayload = {
      ...payload,
      dateOfReservation: dateOfReservationUTC,
      dateEndOfReservation: dateEndOfReservationUTC
    };
    
    // Check if time is being changed
    const originalStart = editingReservation.dateOfReservation ? new Date(editingReservation.dateOfReservation).getTime() : null;
    const originalEnd = editingReservation.dateEndOfReservation ? new Date(editingReservation.dateEndOfReservation).getTime() : null;
    const newStart = processedPayload.dateOfReservation ? new Date(processedPayload.dateOfReservation).getTime() : null;
    const newEnd = processedPayload.dateEndOfReservation ? new Date(processedPayload.dateEndOfReservation).getTime() : null;
    
    const timeChanged = (originalStart && newStart && Math.abs(originalStart - newStart) > 60000) ||
                        (originalEnd && newEnd && Math.abs(originalEnd - newEnd) > 60000);
    
    if (timeChanged) {
      setIsUpdatingTime(true);
    }
    
    try {
      const response = await FetchEditReservations(processedPayload, param.id);
      
      if (response.status === 200 || response.status === 201) {
        // Wait a bit before closing to show success
        setTimeout(() => {
          toastSuccess("Reservierung erfolgreich aktualisiert!");
          setIsUpdatingTime(false);
          handleCloseEditDialog();
          
          // Refresh data
          dispatch(fetchGetAllReservationsByIdApi(param.id));
        }, 1000);
      } else {
        const errorMsg = response?.data?.message || response?.error || `Fehler beim Aktualisieren: Status ${response.status}`;
        toastError(errorMsg);
        setIsUpdatingTime(false);
      }
    } catch (error) {
      console.error("❌ FRONTEND: Exception during update:", error);
      const serverMessage = error?.response?.data?.message || error?.message || "Fehler beim Aktualisieren der Reservierung. Bitte erneut versuchen.";
      toastError(serverMessage);
      setIsUpdatingTime(false);
    } finally {
      setIsLoading(false);
    }
  };

  // مودال التعديل
  const EditDialog = () => {
    const [localFormData, setLocalFormData] = React.useState(editFormData);
    React.useEffect(() => {
      if (editDialogOpen) {
        setLocalFormData(editFormData);
      }
    }, [editDialogOpen, editFormData]);

    const updateField = (field, value) => {
      setLocalFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleLocalSave = () => {
      handleSaveEdit(localFormData);
    };

    return (
    <Dialog 
      open={editDialogOpen} 
      onClose={isUpdatingTime ? undefined : handleCloseEditDialog}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
          position: 'relative'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #D6B981 0%, #C5A572 100%)',
          color: '#fff',
          fontWeight: 'bold'
        }}
      >
        Reservierung bearbeiten Nr.: {editingReservation?.reservationNumber}
      </DialogTitle>
      <DialogContent sx={{ pt: '30px !important', px: 3, pb: 3, backgroundColor: '#faf7ef', position: 'relative' }}>
        {/* Loading Overlay داخل Dialog */}
        {isUpdatingTime && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '0 0 12px 12px'
          }}>
            <CircularProgress size={50} sx={{ color: '#D6B981', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#333', fontWeight: 'bold', mb: 1 }}>
              Termin wird aktualisiert...
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', maxWidth: 300 }}>
              Bitte warten Sie, während wir den Termin aktualisieren und eine E-Mail senden.
            </Typography>
          </Box>
        )}
        
        <Grid container spacing={2} sx={{ mt: 0, opacity: isUpdatingTime ? 0.5 : 1, pointerEvents: isUpdatingTime ? 'none' : 'auto' }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Kundenname"
              value={localFormData.customerName}
              onChange={(e) => updateField('customerName', e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  '&:hover fieldset': { borderColor: '#D6B981' },
                  '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="E-Mail"
              type="email"
              value={localFormData.customerEmail}
              onChange={(e) => updateField('customerEmail', e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  '&:hover fieldset': { borderColor: '#D6B981' },
                  '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Telefonnummer"
              value={localFormData.customerPhone}
              onChange={(e) => updateField('customerPhone', e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  '&:hover fieldset': { borderColor: '#D6B981' },
                  '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Straße und Hausnummer"
              value={localFormData.customerStreet}
              onChange={(e) => updateField('customerStreet', e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  '&:hover fieldset': { borderColor: '#D6B981' },
                  '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Postleitzahl"
              value={localFormData.customerPLZ}
              onChange={(e) => updateField('customerPLZ', e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  '&:hover fieldset': { borderColor: '#D6B981' },
                  '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Stadt"
              value={localFormData.customerCity}
              onChange={(e) => updateField('customerCity', e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  '&:hover fieldset': { borderColor: '#D6B981' },
                  '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={localFormData.status}
                label="Status"
                onChange={(e) => updateField('status', e.target.value)}
                sx={{
                  backgroundColor: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6D7B8' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D6B981' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#C5A572' }
                }}
              >
                <MenuItem value="pending">Ausstehend</MenuItem>
                <MenuItem value="accepted">Angenommen</MenuItem>
                <MenuItem value="rejected">Abgelehnt</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Admin-Notiz"
              multiline
              rows={3}
              value={localFormData.adminNote}
              onChange={(e) => updateField('adminNote', e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  '&:hover fieldset': { borderColor: '#D6B981' },
                  '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Kunden-Notiz"
              multiline
              rows={3}
              value={localFormData.note}
              onChange={(e) => updateField('note', e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  '&:hover fieldset': { borderColor: '#D6B981' },
                  '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                }
              }}
            />
          </Grid>
          
          {/* حقول التاريخ والوقت */}
          <Grid item xs={12}>
            <Box sx={{ 
              p: 2, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #fff8e1 0%, #ffe9a8 100%)',
              border: '2px solid #D6B981'
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#D6B981', mb: 2 }}>
                Datum und Uhrzeit ändern
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                      label="Startdatum und -zeit"
                      value={localFormData.dateOfReservation}
                      onChange={(newValue) => updateField('dateOfReservation', newValue)}
                      format="dd.MM.yyyy HH:mm"
                      ampm={false}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#fff',
                              '&:hover fieldset': { borderColor: '#D6B981' },
                              '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                            }
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                      label="Enddatum und -zeit"
                      value={localFormData.dateEndOfReservation}
                      onChange={(newValue) => updateField('dateEndOfReservation', newValue)}
                      format="dd.MM.yyyy HH:mm"
                      ampm={false}
                      minDateTime={localFormData.dateOfReservation}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#fff',
                              '&:hover fieldset': { borderColor: '#D6B981' },
                              '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                            }
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffc107' }}>
                <Typography variant="caption" sx={{ color: '#856404', display: 'block', fontWeight: 'bold' }}>
                  ⚠️ Hinweis: Wenn Sie das Datum oder die Uhrzeit ändern, wird automatisch eine E-Mail mit den neuen Zeiten an den Kunden gesendet.
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, backgroundColor: '#fff', position: 'relative' }}>
        {isUpdatingTime && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 10,
            borderRadius: '0 0 12px 12px'
          }} />
        )}
        <Button 
          onClick={handleCloseEditDialog} 
          variant="outlined"
          disabled={isUpdatingTime}
          sx={{
            borderColor: '#D6B981',
            color: '#6b5b3e',
            '&:hover': { borderColor: '#C5A572', backgroundColor: '#faf7ef' },
            position: 'relative',
            zIndex: 1
          }}
        >
          Abbrechen
        </Button>
        <Button 
          onClick={handleLocalSave} 
          variant="contained"
          disabled={isLoading || isUpdatingTime}
          sx={{
            background: 'linear-gradient(135deg, #D6B981 0%, #C5A572 100%)',
            color: '#fff',
            fontWeight: 'bold',
            '&:hover': { background: 'linear-gradient(135deg, #C5A572 0%, #B8A572 100%)' },
            position: 'relative',
            zIndex: 1,
            minWidth: 180
          }}
          startIcon={isUpdatingTime ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
        >
          {isUpdatingTime ? 'Wird gespeichert...' : 'Änderungen speichern'}
        </Button>
      </DialogActions>
    </Dialog>
    );
  };

  // تنسيق عرض الأكسترا أوبجكتس
  const renderExtraObjects = () => {
    const extraObjects = data?.data?.data?.extraObjectsList || [];
    
    if (extraObjects.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Keine Extras gebucht
        </Typography>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        {extraObjects.map((item, index) => (
          <Paper 
            key={index} 
            elevation={1} 
            sx={{ 
              p: 2, 
              mb: 1, 
              borderLeft: '4px solid #CD9D41',
              backgroundColor: '#f9f9f9'
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="subtitle2" color="#CD9D41">Name:</Typography>
                <Typography variant="body2">{item.name}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="subtitle2" color="#CD9D41">Anzahl:</Typography>
                <Typography variant="body2">{item.number}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="subtitle2" color="#CD9D41">Preis:</Typography>
                <Typography variant="body2">{item.price}€</Typography>
              </Grid>
            </Grid>
          </Paper>
        ))}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Typography variant="subtitle1" color="#CD9D41">
            Gesamtpreis für Extras: {data?.data?.data?.totalExtraObjectsPrice}€
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {/* Header مع زر التعديل */}
      <Box sx={{ mt: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <AlternativeHeader
          onPrevious={() => navigate("/")}
          title="Einzelheiten"
        />
        
        {data?.data?.data && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{
              background: 'linear-gradient(135deg, #D6B981 0%, #C5A572 100%)',
              color: '#fff',
              fontWeight: 'bold',
              px: 3,
              py: 1,
              '&:hover': { 
                background: 'linear-gradient(135deg, #C5A572 0%, #B8A572 100%)' 
              }
            }}
          >
            Reservierung bearbeiten
          </Button>
        )}
      </Box>
      
      {data?.data?.data && (
        <>
          <ReservationsCard
            reservationNumber={data?.data?.data?.reservationNumber}
            dateOfReservation={formatDateTimeForDisplay(data?.data?.data?.dateOfReservation)}
            firstName={data?.data?.data?.customerName || data?.data?.data?.userId?.firstName || ""}
            lastName={""} // لأن customerName يحتوي على الاسم الكامل
            email={data?.data?.data?.customerEmail || data?.data?.data?.userId?.email || ""}
            customerPhone={data?.data?.data?.customerPhone || ""}
            customerAddress={data?.data?.data?.customerAddress || ""}
            customerStreet={data?.data?.data?.customerStreet || ""}
            customerPLZ={data?.data?.data?.customerPLZ || ""}
            customerCity={data?.data?.data?.customerCity || ""}
            paymentDate={formatDateTimeForDisplay(data?.data?.data?.dateEndOfReservation)}
            status={germanStatus.label}
            statusColor={germanStatus.color}
            payWay={data?.data?.data?.payWay}
            note={data?.data?.data?.note}
            adminNote={data?.data?.data?.adminNote}
            name={data?.data?.data?.packageId?.name || "Standard"}
            numberOfPersons={data?.data?.data?.numberOfPersons}
            extraObjectsList={data?.data?.data?.extraObjectsList}
            totalPackagePrice={data?.data?.data?.totalPackagePrice}
            totalExtraObjectsPrice={data?.data?.data?.totalExtraObjectsPrice}
            totalPrice={data?.data?.data?.totalPrice}
            images={data?.data?.data?.images || []}
            isAGift={data?.data?.data?.isAGift}
            nameOfreceiver={data?.data?.data?.nameOfreceiver}
            emailOfreceiver={data?.data?.data?.emailOfreceiver}
            noteForReceiver={data?.data?.data?.noteForReceiver}
            onClick={() => setModalOpen(true)}
          />

          {/* قسم تفاصيل الإكسترا */}
          <Box sx={{ mt: 3, bgcolor: "white", py: 4, px: 6, borderRadius: "8px" }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: "Noto Sans JP", 
                color: "#CD9D41", 
                fontSize: "22px",
                mb: 2
              }}
            >
              Gebuchte Extras
            </Typography>
            <Divider sx={{ mb: 3, backgroundColor: "#f0f0f0", height: "2px" }} />
            
            {renderExtraObjects()}
          </Box>
        </>
      )}
      
      <NotesModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        notes={adminNote}
        setNotes={setAdminNote}
        handleSave={handleSaveNotes}
        loading={isLoading}
      />
      
      {/* Edit Dialog */}
      <EditDialog />
    </Box>
  );
};

export default ViewReservations;
